import React, { useState, Fragment } from 'react';
import { useNavigate } from "react-router";

// Material
import { Paper, Typography, Grid, Box } from '@mui/material';
import Button from '@mui/material/Button';

// Components
import ViewMovements from './modals';
import ClosedCard from './closedCard';
import ClosedCashType from './closedCashType';
import ArchingTotals from '../../Archings/totals';
import { Spinner } from '@arredo/reactjs-ui';
import ModalSuccess from "../../../components/modal/submit";
import ModalConfirm from '../../../components/modal/confirm';

// Hooks
import useNotify from "../../../hooks/notify";
import useUser from '../../../hooks/user/useUser';
import useCard from "../../../hooks/cards/useCard";
import useTotals from '../../../hooks/cashRegisters/useTotals';
import useCashRegister from "../../../hooks/cashRegisters/useCashRegister";

// Models
import { cashTypes, cashTypesFormat } from '../../../models/CashRegisterMovementEntity';

// Utils
import evnConfig from '../../../config';
import { utils } from '../../../utils/utils';
import { Authenticate } from "../../authentication";

const CreateClosureModal = () => {
    const { Notify, variants } = useNotify();
    const navigate  = useNavigate();
    const { cashRegister, createClosure } = useCashRegister();
    const { totals, loading: loadingArching } = useTotals(cashRegister?.id);
    const { cardsTotals, cardsTotalsValids, loading: loadingCard, edit } = useCard(cashRegister?.id);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [cashType, setCashType] = useState(null);
    const [diffCard, setDiffCard] = useState(0);
    const diffMin = evnConfig.MIN_DIFF_CLOSING;
    const { business } = useUser();

    const [archingDetails, setArchingDetails] = useState(
        Object.keys(cashTypesFormat).reduce((acum, curr) => ({
            ...acum,
            [curr]: {
                total: '',
                real: ''
            }
        }), {})
    )

    const mergeValidCards = () => {
        const result = cardsTotals;
        cardsTotalsValids.cards.forEach(i => {
            const index = result.cards.find(j => j.id === i.id);
            result.cards[index] = { ...i };
        });

        return result;
    }

    const handleInputChange = (event) => {
        const value = event.target.value;
        if (!utils.ValidateValueIsString(value))
            if (value >= 0) {
                setArchingDetails({
                    ...archingDetails,
                    [event.target.name]: {
                        total: totals[event.target.name] ? totals[event.target.name].total : 0,
                        real: value,
                        currencyPrice: totals[event.target.name]?.price ?? 0,
                        lastClosureAmount: totals[event.target.name] ? totals[event.target.name].lastClosureAmount : 0
                    }
                })
            }
    }

    const saveCard = (value, cardId) => {
        edit(value, cardId);
    }

    const onCancel = () => {
        setConfirmOpen(false);
    }

    const handleSave = async () => {
        setConfirmOpen(false)

        try {
            const response = await Authenticate();
            if (!response.account)
                throw new Error("Error al intentar realizar el logueo");

            archingDetails.Card = mergeValidCards();
            setSubmitOpen(true);
            const closure = await createClosure(archingDetails, response.account.username);

            if (!closure)
                Notify(`Se produjo un error al intentar crear el cierre`, variants.ERROR);

            setSuccess(!!closure);
            setError(!closure)
        } catch (err) {
            setSubmitOpen(false);
            setSuccess(false);
            setError(false);
            Notify(err.message, variants.ERROR);
        }
    }

    const handleError = async (event) => {
        setSubmitOpen(false);
        setSuccess(false);
        setError(false);
        Notify("Error al intentar realizar el cierre de caja", variants.ERROR);
    }

    const validateData = () => {
        const validCashTypes = business?.currencies?.every(i => {
            const key = "Cash" + i;
            return Number(archingDetails[key].total) > 0 ? Number(archingDetails[key].real) > 0 : archingDetails[key].real !== '' && Number(archingDetails[key].real) >= 0;
        });
       
        return (validCashTypes) &&
            (totals?.Qr?.total > 0 ? Number(archingDetails.Qr.real) > 0 : Number(archingDetails.Qr.real) >= 0) &&
            (totals?.Link?.total > 0 ? Number(archingDetails.Link.real) > 0 : Number(archingDetails.Link.real) >= 0) &&
            (totals?.Check?.total > 0 ? Number(archingDetails.Check.real) > 0 : Number(archingDetails.Check.real) >= 0) &&
            (totals?.TerminalQr?.total > 0 ? Number(archingDetails.TerminalQr.real) > 0 : Number(archingDetails.TerminalQr.real) >= 0) &&
             cardsTotalsValids.cards?.every(i =>
                 i.subType?.Sale?.realTotal >= 0 && i.subType?.Sale?.realCount >= 0 &&
                 i.subType?.Void?.realTotal >= 0 && i.subType?.Void?.realCount >= 0 &&
                 i.subType?.Refund?.realTotal >= 0 && i.subType?.Refund?.realCount >= 0
             )
    }

    const DiffClosure = () => {
        const cash = business?.currencies?.some(i => {
            const key = "Cash" + i;
            return Math.abs(archingDetails[key].total - archingDetails[key].real) >= diffMin;
        });
        const check = Math.abs(archingDetails.Check?.total - archingDetails?.Check.real) >= diffMin;
        const link = Math.abs(archingDetails.Link?.total - archingDetails?.Link.real) >= diffMin;
        const qr = Math.abs(archingDetails.Qr?.total - archingDetails?.Qr.real) >= diffMin;
        const terminalQr = Math.abs(archingDetails.TerminalQr?.total - archingDetails?.TerminalQr.real) >= diffMin;
        return cash || check || link || qr || terminalQr || diffCard >= diffMin;
    }

    const VeriDiffClosure = () =>
        DiffClosure()
            ? setConfirmOpen(true)
            : handleSave();

    return (
        <>
            <Grid container direction='column' spacing={1}>
                <Grid>
                    <Box component={Paper} padding={2}>
                        <Typography variant="h4" align='center'>
                            {`Nuevo cierre: ${cashRegister.name}`}
                        </Typography>
                    </Box>
                </Grid>
                {loadingArching && <Grid> <Spinner message="Cargando" /> </Grid>}
                {
                    !loadingArching &&
                    <>
                        <Grid>
                            <ArchingTotals
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCashType
                                title="QR"
                                cashType={cashTypes.qr}
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCashType
                                title="Terminal Qr"
                                cashType={cashTypes.terminalQr}
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCashType
                                title="Link"
                                cashType={cashTypes.link}
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCashType
                                title="Cheques"
                                cashType={cashTypes.check}
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCashType
                                title="Transferencias"
                                cashType={cashTypes.transfer}
                                totals={totals}
                                detail={archingDetails}
                                onInputChange={handleInputChange}
                                viewMovements={(cashType) => setCashType(cashType)}
                            />
                        </Grid>
                        <Grid>
                            <ClosedCard
                                cardsTotals={cardsTotalsValids}
                                onAccept={saveCard}
                                loadingCard={loadingCard}
                                viewMovements={(cashType) => setCashType(cashType)}
                                setDiffCard={setDiffCard}
                            />
                        </Grid>
                    </>
                }
                <Grid>
                    <Box component={Paper} padding={2} display='flex' justifyContent='space-between'>
                        <Button
                            onClick={() => navigate(-1)}
                            color="primary"
                            variant='outlined'
                        >
                            Cancelar
                        </Button>
                        <Button
                            disabled={!validateData()}
                            onClick={VeriDiffClosure}
                            color='primary'
                            variant='contained'
                            autoFocus
                        >
                            Guardar
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            {
                Boolean(cashType) &&
                <ViewMovements
                    open={Boolean(cashType)}
                    cashType={cashType.cashType}
                    cashTypeId={cashType.cashTypeId}
                    cashRegisterId={cashRegister.id}
                    lastClousereDate={cashRegister.lastClosure}
                    onClose={() => setCashType(null)}
                />
            }
            <ModalSuccess
                open={submitOpen}
                success={success}
                error={error}
                successText="Cierre creado con éxito"
                onSuccess={() => navigate(`/cashRegisters/closures`)}
                errorText="No se pudo crear el cierre"
                onError={handleError}
            />
            <ModalConfirm
                title="Confirmacion cierre de caja"
                open={confirmOpen}
                message={<>
                    <p>{`Hay diferencia/s igual o superior a $${utils.formatMoney(diffMin)}`}</p>
                    <p>¿Está seguro/a de realizar el cierre?</p>
                </>}
                onAccept={handleSave}
                onCancel={onCancel}
            />
        </>
    );
}

export default CreateClosureModal;